import { useEffect, useState } from "react";

import { CRM } from "@/components/atomic-crm/root/CRM";
import {
  ApexHostError,
  ReservedHostError,
  WorkspaceNotFoundError,
  resolveWorkspaceConfig,
} from "@/components/atomic-crm/providers/supabase/workspaceConfig";
import { initSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { initTelemetry } from "@/components/atomic-crm/telemetry";
import darkModeLogo from "@/components/atomic-crm/root/logos/logo_obvious_dark.svg";

/**
 * Application entry point for deployed workspaces.
 *
 * One static build serves every tenant subdomain (docs/decisions.md #22):
 * before rendering, resolve which Supabase project this hostname belongs
 * to - control-plane registry on tenant subdomains, build-time env vars as
 * the fallback (today's single-tenant deployment) - then boot the CRM.
 */

const WorkspaceNotFound = ({ slug }: { slug: string }) => (
  <div style={{ maxWidth: 480, margin: "18vh auto", padding: "0 24px", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
    <h1 style={{ fontSize: 22, marginBottom: 8 }}>Workspace not found</h1>
    <p style={{ fontSize: 15, color: "#5f6b7a", lineHeight: 1.5 }}>
      There's no workspace at <strong>{slug}</strong>.obviouscrm.com. Check
      the address with your team, or reach out to Grey Matter if you think
      this is a mistake.
    </p>
  </div>
);

const ApexHost = () => (
  <div
    style={{
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <img
      src={darkModeLogo}
      alt="Obvious CRM"
      width={160}
      className="filter brightness-0 dark:invert"
    />
  </div>
);

const ReservedHost = () => (
  <div style={{ maxWidth: 500, margin: "18vh auto", padding: "0 24px", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
    <h1 style={{ fontSize: 22, marginBottom: 8 }}>This address is reserved</h1>
    <p style={{ fontSize: 15, color: "#5f6b7a", lineHeight: 1.5 }}>
      This subdomain belongs to the Grey Matter operator console, which runs
      as its own application. Looking for your CRM? Your workspace lives at{" "}
      <strong>yourcompany</strong>.obviouscrm.com.
    </p>
  </div>
);

const ConfigError = ({ message }: { message: string }) => (
  <div style={{ maxWidth: 520, margin: "18vh auto", padding: "0 24px", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
    <h1 style={{ fontSize: 22, marginBottom: 8 }}>Workspace unavailable</h1>
    <p style={{ fontSize: 15, color: "#5f6b7a", lineHeight: 1.5 }}>{message}</p>
  </div>
);

const App = () => {
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "ready" }
    | { phase: "not-found"; slug: string }
    | { phase: "reserved" }
    | { phase: "apex" }
    | { phase: "error"; message: string }
  >({ phase: "loading" });

  useEffect(() => {
    resolveWorkspaceConfig()
      .then((config) => {
        initSupabaseClient(config.supabaseUrl, config.publishableKey);
        // Usage/error telemetry (decision #43): only when this build knows
        // its workspace slug and the services host - never in the demo.
        const servicesUrl = import.meta.env.VITE_CONTROL_PLANE_URL;
        if (config.slug && servicesUrl) {
          initTelemetry({ slug: config.slug, servicesUrl });
        }
        setState({ phase: "ready" });
      })
      .catch((e) => {
        if (e instanceof WorkspaceNotFoundError)
          setState({ phase: "not-found", slug: e.slug });
        else if (e instanceof ReservedHostError)
          setState({ phase: "reserved" });
        else if (e instanceof ApexHostError) setState({ phase: "apex" });
        else setState({ phase: "error", message: String(e?.message ?? e) });
      });
  }, []);

  if (state.phase === "loading") return null;
  if (state.phase === "not-found") return <WorkspaceNotFound slug={state.slug} />;
  if (state.phase === "reserved") return <ReservedHost />;
  if (state.phase === "apex") return <ApexHost />;
  if (state.phase === "error") return <ConfigError message={state.message} />;
  return <CRM />;
};

export default App;
